import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CONTENT_TEAMS, ContentTeamConfig } from "../../config/contentTeams";
import { admins, currentSeason } from "../../Constants";
import { AuthContext } from "../../context/AuthContext";
import { StandingsPrediction } from "../../models/StandingsPrediction";
import { Standing } from "../../models/Standing";
import {
  getAllUserPredictions,
  getDivisionStandingsAll,
  getPredictionLeaderboard,
  getUserPredictionsStrict,
  PredictionLeaderboardRow,
} from "../../services/MatchService";
import { getAccountBySteamId } from "../../services/FantasyService";
import { getOGCGuest, OGCGuest } from "../../services/OGCGuestService";
import { hasGamesPlayed, scorePrediction } from "../../utils/predictionScore";
import { getStandingZone, ZONE_LABEL_KEYS } from "../../utils/standingZones";
import Loading from "../../components/Loading";
import {
  divisionLabel,
  FIRST_PREDICTION_SEASON,
  PredictionsByDivision,
  predictableDivisions,
  StandingsByDivision,
  toPredictionMap,
} from "./predict/predictShared";
import {
  colorVars,
  Column,
  ColumnColor,
  DivisionResult,
  GUEST_COLOR,
  HOST_COLORS,
  makeColumn,
  RIVAL_COLORS,
} from "./contentTeam/contentTeamShared";
import HostAvatar from "./contentTeam/HostAvatar";
import SeasonHeadToHead, { HostSeason } from "./contentTeam/SeasonHeadToHead";
import PodcastDuel, { DuelTeam, DuelResult } from "./contentTeam/PodcastDuel";
import DivisionTabs from "./contentTeam/DivisionTabs";
import ScoreCards from "./contentTeam/ScoreCards";
import ComparisonTable, { TableSort } from "./contentTeam/ComparisonTable";
import TeamPanel from "./contentTeam/TeamPanel";
import GuestPicker from "./contentTeam/GuestPicker";
import styles from "./contentTeam/ContentTeam.module.css";

const SEASONS = Array.from({ length: currentSeason - FIRST_PREDICTION_SEASON + 1 }, (_, i) => currentSeason - i);

type LoadState = "loading" | "ready" | "error";

interface GuestState {
  key: string;
  guest: OGCGuest | null;
  prediction: StandingsPrediction | null;
}

/**
 * /ogc, /tun-unc (and /ogc2, /tun-unc2) — a podcast's hosts' standings predictions side by side against the live table,
 * with a season head-to-head on top and an optional guest per division. ?season= and ?division=
 * keep the view in the URL. A team with `compareWith` can add the other podcast's hosts' scores
 * (score cards only) plus a podcast-vs-podcast panel (?vs=<teamId>).
 */
const ContentTeamPage2: React.FC<{ config: ContentTeamConfig }> = ({ config }) => {
  const { t } = useTranslation("pages");
  const { user, token } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const hostIds = config.hostIds;
  const rival = useMemo(
    () => CONTENT_TEAMS.find((team) => team.teamId === config.compareWith && team.teamId !== config.teamId) ?? null,
    [config]
  );
  const compare = !!rival && searchParams.get("vs") === rival.teamId;
  const rivalIds = useMemo(() => (compare && rival ? rival.hostIds.filter((id) => !hostIds.includes(id)) : []), [compare, rival, hostIds]);
  const predictorIds = useMemo(() => [...hostIds, ...rivalIds], [hostIds, rivalIds]);

  const seasonParam = Number(searchParams.get("season"));
  const season = SEASONS.includes(seasonParam) ? seasonParam : currentSeason;
  const divisionParam = searchParams.get("division") ?? "";

  // Each result is stored with the season it belongs to, so a slow response for a season you just
  // left is never drawn over the one you switched to.
  const [standings, setStandings] = useState<{ season: number; data: StandingsByDivision } | null>(null);
  const [standingsState, setStandingsState] = useState<LoadState>("loading");
  const [standingsReload, setStandingsReload] = useState(0);
  const [hostPredictions, setHostPredictions] = useState<{ season: number; byHost: Record<string, PredictionsByDivision | null> } | null>(null);
  const [board, setBoard] = useState<{ season: number; rows: PredictionLeaderboardRow[] | null } | null>(null);
  // An id is missing until its account lookup settles (null when it failed), so a name never
  // flashes up as a Steam id while loading.
  const [accountNames, setAccountNames] = useState<Record<string, string | null>>({});
  const [guest, setGuest] = useState<GuestState | null>(null);
  const [guestReload, setGuestReload] = useState(0);
  const [selected, setSelected] = useState<{ key: string; teamId: string } | null>(null);
  const [sort, setSort] = useState<{ key: string; sort: TableSort } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStandingsState("loading");
    getDivisionStandingsAll(season)
      .then((data: StandingsByDivision) => {
        if (cancelled) return;
        setStandings({ season, data: data ?? {} });
        setStandingsState("ready");
      })
      .catch(() => {
        if (!cancelled) setStandingsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [season, standingsReload]);

  // Merged into what the season already has, so turning compare mode on doesn't blank the hosts'
  // columns while the other team's predictions load.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled(predictorIds.map((id) => getUserPredictionsStrict(id, season))).then((results) => {
      if (cancelled) return;
      const byHost: Record<string, PredictionsByDivision | null> = {};
      results.forEach((r, i) => {
        byHost[predictorIds[i]] = r.status === "fulfilled" ? toPredictionMap(r.value) : null;
      });
      setHostPredictions((prev) => ({ season, byHost: prev?.season === season ? { ...prev.byHost, ...byHost } : byHost }));
    });
    return () => {
      cancelled = true;
    };
  }, [season, predictorIds]);

  useEffect(() => {
    let cancelled = false;
    getPredictionLeaderboard(season)
      .then((rows) => {
        if (!cancelled) setBoard({ season, rows });
      })
      .catch(() => {
        if (!cancelled) setBoard({ season, rows: null });
      });
    return () => {
      cancelled = true;
    };
  }, [season]);

  // Names are shown even when an account lookup fails: the leaderboard row, then the Steam id.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled(predictorIds.map((id) => getAccountBySteamId(id))).then((results) => {
      if (cancelled) return;
      const names: Record<string, string | null> = {};
      results.forEach((r, i) => {
        names[predictorIds[i]] = r.status === "fulfilled" && r.value?.playerName ? r.value.playerName : null;
      });
      setAccountNames((prev) => ({ ...prev, ...names }));
    });
    return () => {
      cancelled = true;
    };
  }, [predictorIds]);

  const table = standings?.season === season ? standings.data : null;
  const divisions = useMemo(() => predictableDivisions(table), [table]);
  const division = divisions.includes(divisionParam) ? divisionParam : divisions[0] ?? "";
  const viewKey = `${season}|${division}`;
  const rows: Standing[] = useMemo(
    () => [...(table?.[division] ?? [])].sort((a, b) => a.rank - b.rank),
    [table, division]
  );
  const played = hasGamesPlayed(rows);
  const boardRows = board?.season === season ? board.rows : undefined;
  const predictionsByHost = hostPredictions?.season === season ? hostPredictions.byHost : null;

  useEffect(() => {
    if (!division) return;
    let cancelled = false;
    const key = `${season}|${division}`;
    (async () => {
      const found = await getOGCGuest(season, division, config.teamId);
      if (cancelled) return;
      if (!found.hasGuest || !found.guest) {
        setGuest({ key, guest: null, prediction: null });
        return;
      }
      const predictions = await getAllUserPredictions(found.guest.guestSteamId, season, division);
      if (cancelled) return;
      setGuest({
        key,
        guest: found.guest,
        prediction: predictions.find((p) => p.division === division && p.teams.length > 0) ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [season, division, config.teamId, guestReload]);
  const shownGuest = guest?.key === viewKey ? guest : null;

  const hostName = useCallback(
    (id: string): string =>
      accountNames[id] ?? boardRows?.find((r) => r.steamId === id)?.playerName ?? (id in accountNames ? id : "…"),
    [accountNames, boardRows]
  );

  const columns: Column[] = useMemo(() => {
    const list = hostIds.map((id, i) => {
      const byDivision = predictionsByHost ? predictionsByHost[id] : undefined;
      return makeColumn(
        {
          key: id,
          steamId: id,
          name: hostName(id),
          kind: "host",
          color: HOST_COLORS[i % HOST_COLORS.length],
          loading: byDivision === undefined,
          failed: byDivision === null,
        },
        byDivision ? byDivision[division] : null,
        rows
      );
    });
    if (shownGuest?.guest) {
      list.push(
        makeColumn(
          {
            key: "guest",
            steamId: shownGuest.guest.guestSteamId,
            name: shownGuest.guest.guestName,
            kind: "guest",
            color: GUEST_COLOR,
            loading: false,
            failed: false,
          },
          shownGuest.prediction,
          rows
        )
      );
    }
    rivalIds.forEach((id, i) => {
      const byDivision = predictionsByHost ? predictionsByHost[id] : undefined;
      list.push(
        makeColumn(
          {
            key: id,
            steamId: id,
            name: hostName(id),
            kind: "rival",
            team: rival?.heading,
            color: RIVAL_COLORS[i % RIVAL_COLORS.length],
            loading: byDivision === undefined,
            failed: byDivision === null,
          },
          byDivision ? byDivision[division] : null,
          rows
        )
      );
    });
    return list;
  }, [hostIds, rivalIds, rival, predictionsByHost, hostName, division, rows, shownGuest]);
  // The compared team only gets its score cards; the table and team panel stay this podcast's.
  const guessColumns = useMemo(() => columns.filter((c) => c.kind !== "rival"), [columns]);

  /** Every division's score for each of `ids`, in that order (null where there is no prediction). */
  const seasonResults = useCallback(
    (ids: string[]): DivisionResult[] =>
      divisions.map((d) => ({
        division: d,
        scores: ids.map((id) => {
          const prediction = predictionsByHost?.[id]?.[d];
          return prediction && table ? scorePrediction(prediction.teams, table[d]) : null;
        }),
      })),
    [divisions, predictionsByHost, table]
  );

  /** A predictor's season line from the leaderboard, or the same average over its scores in `results` (slot `i`). */
  const seasonOf = useCallback(
    (id: string, color: ColumnColor, results: DivisionResult[], i: number): HostSeason => {
      const index = boardRows ? boardRows.findIndex((r) => r.steamId === id) : -1;
      const row = index >= 0 && boardRows ? boardRows[index] : null;
      const mine = results.map((r) => r.scores[i]).filter((s): s is NonNullable<typeof s> => !!s);
      return {
        steamId: id,
        name: hostName(id),
        color,
        rank: row ? index + 1 : null,
        avg: row ? row.averageScore : mine.length ? mine.reduce((sum, s) => sum + s.score, 0) / mine.length : null,
        exact: row ? row.exactHits ?? null : mine.length ? mine.reduce((sum, s) => sum + s.exact, 0) : null,
      };
    },
    [boardRows, hostName]
  );

  const headToHead = useMemo(() => {
    if (hostIds.length !== 2 || !table || divisions.length === 0) return null;
    const results = seasonResults(hostIds);
    const hosts = hostIds.map((id, i) => seasonOf(id, HOST_COLORS[i], results, i));
    return { results, hosts, predictors: boardRows?.length ?? 0 };
  }, [hostIds, table, divisions, seasonResults, seasonOf, boardRows]);

  // Compare mode: a team's division score is the mean of its hosts' scores there.
  const duel = useMemo(() => {
    if (!rival || rivalIds.length === 0 || !table || divisions.length === 0) return null;
    const results = seasonResults(predictorIds);
    const n = hostIds.length;
    const teamScore = (scores: DivisionResult["scores"]): number | null => {
      const scored = scores.filter((s): s is NonNullable<typeof s> => !!s);
      return scored.length ? scored.reduce((sum, s) => sum + s.score, 0) / scored.length : null;
    };
    const teams: [DuelTeam, DuelTeam] = [
      { name: config.heading, predictors: hostIds.map((id, i) => seasonOf(id, HOST_COLORS[i % HOST_COLORS.length], results, i)) },
      { name: rival.heading, predictors: rivalIds.map((id, i) => seasonOf(id, RIVAL_COLORS[i % RIVAL_COLORS.length], results, n + i)) },
    ];
    const duelResults: DuelResult[] = results.map((r) => ({
      division: r.division,
      scores: [teamScore(r.scores.slice(0, n)), teamScore(r.scores.slice(n))],
    }));
    return { teams, results: duelResults, predictors: boardRows?.length ?? 0 };
  }, [rival, rivalIds, predictorIds, hostIds, table, divisions, seasonResults, seasonOf, boardRows, config.heading]);

  const selectedTeamId =
    selected?.key === viewKey && rows.some((r) => r.team.teamId === selected.teamId) ? selected.teamId : rows[0]?.team.teamId;
  const selectedIndex = rows.findIndex((r) => r.team.teamId === selectedTeamId);
  const selectedStanding = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const selectedZone = selectedStanding ? getStandingZone(season, division, selectedIndex, selectedStanding) : "white";
  const zoneLabel =
    selectedZone === "white"
      ? null
      : selectedZone === "green" && division === "ligaen" && season >= 33
        ? t("predict.zoneLan")
        : t(ZONE_LABEL_KEYS[selectedZone]);
  const tableSort: TableSort = sort?.key === viewKey ? sort.sort : { by: "rank", dir: 1 };

  const setView = (nextSeason: number, nextDivision: string) => {
    const params: Record<string, string> = {};
    if (nextSeason !== currentSeason) params.season = String(nextSeason);
    if (nextDivision) params.division = nextDivision;
    if (compare && rival) params.vs = rival.teamId;
    setSearchParams(params, { replace: true });
  };
  const toggleCompare = () => {
    if (!rival) return;
    const params = new URLSearchParams(searchParams);
    if (compare) params.delete("vs");
    else params.set("vs", rival.teamId);
    setSearchParams(params, { replace: true });
  };
  const onSort = (by: string) =>
    setSort({ key: viewKey, sort: tableSort.by === by ? { by, dir: tableSort.dir === 1 ? -1 : 1 } : { by, dir: 1 } });

  const canManage = !!user && (hostIds.includes(user.steamID) || admins.includes(user.steamID));
  const divisionName = divisionLabel(division);

  const seasonPills = (
    <div className={styles.pillGroup} role="group" aria-label={t("contentTeam.season")}>
      {SEASONS.map((s) => (
        <button
          key={s}
          type="button"
          className={`${styles.pill} ${s === season ? styles.pillOn : ""}`}
          aria-pressed={s === season}
          onClick={() => setView(s, division)}
        >
          {s === season ? t("contentTeam.seasonPill", { season: s }) : s}
        </button>
      ))}
    </div>
  );

  // The title sits on top of the season panel, whose host sides already name the hosts; the chips
  // only show when there is no head-to-head (still loading, or a team with more than two hosts).
  const header = (
    <header className={styles.heroTop}>
      <div className={styles.headText}>
        <span className={styles.eyebrow}>{t("contentTeam.eyebrow")}</span>
        <h1 className={styles.title}>{config.heading}</h1>
        <p className={styles.subtitle}>
          {season === currentSeason ? t("contentTeam.subtitleLive", { season }) : t("contentTeam.subtitlePast", { season })}
        </p>
        {table && !headToHead && (
          <div className={styles.hostChips}>
            {hostIds.map((id, i) => (
              <span key={id} className={styles.hostChip} style={colorVars(HOST_COLORS[i % HOST_COLORS.length])}>
                <HostAvatar steamId={id} name={hostName(id)} color={HOST_COLORS[i % HOST_COLORS.length]} size="sm" />
                {hostName(id)}
                <span className={styles.hostChipTag}>{t("contentTeam.hostTag")}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={styles.heroControls}>
        {rival && (
          <button type="button" className={`${styles.ghostBtn} ${styles.compareBtn}`} aria-pressed={compare} onClick={toggleCompare}>
            <span className={styles.compareCheck} aria-hidden="true">
              {compare && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </span>
            {t("contentTeam.compareWith", { name: rival.heading })}
          </button>
        )}
        {seasonPills}
      </div>
    </header>
  );

  return (
    <div className={styles.page}>
      {headToHead ? (
        <SeasonHeadToHead season={season} hosts={headToHead.hosts} results={headToHead.results} predictors={headToHead.predictors}>
          {header}
        </SeasonHeadToHead>
      ) : (
        <section className={`${styles.panel} ${styles.heroPanel}`}>{header}</section>
      )}

      {duel && <PodcastDuel season={season} teams={duel.teams} results={duel.results} predictors={duel.predictors} />}

      {standingsState === "loading" && !table && <Loading />}

      {standingsState === "error" && !table && (
        <div className={`${styles.panel} ${styles.notice}`}>
          <span>{t("contentTeam.loadError")}</span>
          <button type="button" className={styles.ghostBtn} onClick={() => setStandingsReload((n) => n + 1)}>
            {t("contentTeam.retry")}
          </button>
        </div>
      )}

      {table && divisions.length === 0 && (
        <div className={`${styles.panel} ${styles.notice}`}>{t("contentTeam.noDivisions", { season })}</div>
      )}

      {table && divisions.length > 0 && (
        <>
          <DivisionTabs
            divisions={divisions}
            selected={division}
            onSelect={(d) => setView(season, d)}
            hosts={headToHead?.hosts}
            results={headToHead?.results}
            right={
              canManage && user ? (
                <GuestPicker
                  key={viewKey}
                  season={season}
                  division={division}
                  divisionName={divisionName}
                  contentTeam={config.teamId}
                  hostSteamId={user.steamID}
                  token={token}
                  guest={shownGuest?.guest ?? null}
                  onChanged={() => setGuestReload((n) => n + 1)}
                />
              ) : undefined
            }
          />

          <ScoreCards columns={columns} total={rows.length} played={played} />

          <div className={styles.main}>
            <ComparisonTable
              season={season}
              division={division}
              divisionName={divisionName}
              standings={rows}
              columns={guessColumns}
              played={played}
              sort={tableSort}
              onSort={onSort}
              selectedTeamId={selectedTeamId}
              onSelect={(teamId) => setSelected({ key: viewKey, teamId })}
            />
            {selectedStanding && (
              <TeamPanel
                season={season}
                division={division}
                divisionName={divisionName}
                standing={selectedStanding}
                zoneLabel={zoneLabel}
                columns={guessColumns}
                played={played}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContentTeamPage2;
